import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, apiError, setToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft, Copy } from "lucide-react";

export function AuthFrame({ title, subtitle, children, testid }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden" style={{ background: "var(--ink)" }}>
      <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(700px 300px at 80% 0%, rgba(18,179,166,.35), transparent 60%), radial-gradient(500px 300px at 0% 100%, rgba(224,118,43,.25), transparent 60%)" }} />
      <div className="relative w-full max-w-md panel p-8 rise" data-testid={testid}>
        <Link to="/login" className="flex items-center gap-2 mb-6"><span className="w-8 h-8 rounded-lg flex items-center justify-center font-heading font-bold text-white" style={{ background: "var(--signal)" }}>A</span><span className="font-heading font-semibold">Atlas</span></Link>
        <h1 className="font-heading text-2xl font-semibold">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
        <div className="mt-6 space-y-4">{children}</div>
      </div>
    </div>
  );
}

export function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(null);
  const [name, setName] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/auth/invitations/${token}`).then((r) => { setInfo(r.data); setName(r.data.name || ""); }).catch((e) => setErr(apiError(e))); }, [token]);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try { const { data } = await api.post(`/auth/invitations/${token}/accept`, { password: pw, name }); setToken(data.token); toast.success("Account activated"); window.location.href = "/"; }
    catch (e2) { toast.error(apiError(e2)); } finally { setBusy(false); }
  };
  if (err) return <AuthFrame title="Invitation unavailable" subtitle={err} testid="invite-error"><Link to="/login" className="text-sm underline">Back to sign in</Link></AuthFrame>;
  if (!info) return <AuthFrame title="Checking invitation…" testid="invite-loading" />;
  if (info.used || info.expired) return <AuthFrame title={info.used ? "Invitation already used" : "Invitation expired"} subtitle="Ask your administrator to reissue the invitation." testid="invite-invalid"><Link to="/login" className="text-sm underline">Back to sign in</Link></AuthFrame>;
  return (
    <AuthFrame title={`Join ${info.tenant}`} subtitle={`You've been invited as ${info.email}. Set a password to activate your account.`} testid="accept-invite-page">
      <form onSubmit={submit} className="space-y-4">
        <div><Label>Your name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="invite-name" /></div>
        <div><Label>Password</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8} data-testid="invite-password" placeholder="At least 8 characters" /></div>
        <div><Label>Confirm password</Label><Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required data-testid="invite-password-confirm" /></div>
        <Button type="submit" className="w-full rounded-full" disabled={busy} data-testid="invite-submit"><ShieldCheck className="w-4 h-4 mr-1" /> {busy ? "Activating…" : "Activate & sign in"}</Button>
      </form>
    </AuthFrame>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => { e.preventDefault(); setBusy(true); try { const { data } = await api.post("/auth/forgot-password", { email }); setRes(data); } catch (e2) { toast.error(apiError(e2)); } finally { setBusy(false); } };
  return (
    <AuthFrame title="Reset your password" subtitle="Enter your email and we'll prepare a secure reset link." testid="forgot-page">
      {res ? (
        <div className="space-y-3" data-testid="forgot-result">
          <p className="text-sm text-slate-700">{res.message}</p>
          {res.reset_link && <div className="rounded-xl p-3 border bg-[color:var(--paper-2)]" style={{ borderColor: "var(--line)" }}><p className="eyebrow mb-1">Email could not be delivered · use this link</p><div className="flex gap-2"><Input readOnly value={res.reset_link} className="font-mono text-xs" data-testid="reset-link-value" /><Button variant="outline" size="icon" onClick={() => { navigator.clipboard?.writeText(res.reset_link); toast.success("Copied"); }}><Copy className="w-4 h-4" /></Button></div><a href={res.reset_link} className="text-xs underline mt-2 inline-block" data-testid="reset-link-open">Open reset link</a></div>}
          <Link to="/login" className="text-sm flex items-center gap-1 text-slate-600"><ArrowLeft className="w-3.5 h-3.5" /> Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email" /></div>
          <Button type="submit" className="w-full rounded-full" disabled={busy} data-testid="forgot-submit">{busy ? "Preparing…" : "Send reset link"}</Button>
          <Link to="/login" className="text-sm flex items-center gap-1 text-slate-600 justify-center"><ArrowLeft className="w-3.5 h-3.5" /> Back to sign in</Link>
        </form>
      )}
    </AuthFrame>
  );
}

export function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try { await api.post("/auth/reset-password", { token, password: pw }); toast.success("Password updated — sign in"); navigate("/login"); } catch (e2) { toast.error(apiError(e2)); } finally { setBusy(false); }
  };
  return (
    <AuthFrame title="Choose a new password" testid="reset-page">
      <form onSubmit={submit} className="space-y-4">
        <div><Label>New password</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8} data-testid="reset-password" /></div>
        <div><Label>Confirm</Label><Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required data-testid="reset-password-confirm" /></div>
        <Button type="submit" className="w-full rounded-full" disabled={busy} data-testid="reset-submit">{busy ? "Updating…" : "Update password"}</Button>
      </form>
    </AuthFrame>
  );
}
